import React, { useState, useEffect, useRef } from "react";
import {
    Table,
    Input,
    Button,
    Space,
    Spin, DatePicker, Select
} from "antd";
import { message } from "../../components/antdMessageBridge";
import { SearchOutlined } from "@ant-design/icons";
import { FileTextOutlined } from '@ant-design/icons'; // Optional icon
import CommonObj from "../../CommonObj";
import { useAuthToken } from "../../components/usecontext/AuthContext";
import "./legalBatchService.css";
import { exportToExcel } from '../exporttoexcel/recycleexporttoexcel';
import { runGridExport } from "../../components/gridExportHelper";
import { encryptTimestamp } from "../../components/commoncsrf/csrf-common";
import dayjs from 'dayjs';
import { Link, useLocation, useNavigationType } from "react-router-dom";
import "../../components/commonpagination/common-pagination.css";

const LegalBatchService = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const { accessToken } = useAuthToken();
    const searchInput = useRef(null);
    const location = useLocation();
    const navType = useNavigationType();
    const isBrowserBack = navType === "POP";
    const returningFromChild = location.state?.fromChild && !isBrowserBack;
    const [searchFilters, setsearchFilters] = useState(returningFromChild ? location.state?.searchFilters : {});
    const [sortedInfo, setSortedInfo] = useState(returningFromChild ? location.state?.sortedInfo : {});
    const [pagination, setPagination] = useState(returningFromChild ? location.state?.pagination : {
        current: 1,
        pageSize: 10,
        total: 0
    });
    const fromChild = true;
    const handleReset = (dataIndex, clearFilters) => {
        clearFilters();
        setsearchFilters((prev) => {
            const updated = { ...prev };
            delete updated[dataIndex];
            return updated;
        });
        fetchData({
            pagination,
            sorter: sortedInfo,
            filters: { ...searchFilters, [dataIndex]: undefined },
        });
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    style={{ marginBottom: 8, display: "block" }}
                    onPressEnter={() => confirm()}
                />
                <Space>
                    <Button type="primary" size="small" onClick={() => confirm()}>Search</Button>
                    <Button size="small" onClick={() => {
                        handleReset(dataIndex, clearFilters);
                        confirm();
                    }}>Reset</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />),
        filteredValue: searchFilters[dataIndex] ? [searchFilters[dataIndex]] : null,
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        sorter: true,
        sortOrder: sortedInfo.columnKey === dataIndex ? sortedInfo.order : null
    });
    const disableFutureDates = (current) => {
        const today = new Date();
        return current && current > today;
    };
    const getDateColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
            <div style={{ padding: 8 }}>
                <DatePicker
                    ref={searchInput}
                    value={selectedKeys[0] ? dayjs(selectedKeys[0]) : null}
                    onChange={(date) => {
                        if (date) {
                            const dateString = date.format('YYYY-MM-DD');
                            if (dataIndex === 'startTimeCST' && searchFilters.finishTimeCST) {
                                if (date.isAfter(dayjs(searchFilters.finishTimeCST))) {
                                    message.error("Start date cannot be greater than end date");
                                    setSelectedKeys([]);
                                    handleReset(dataIndex, clearFilters);
                                    return;
                                }
                            }
                            if (dataIndex === 'finishTimeCST' && searchFilters.startTimeCST) {
                                if (date.isBefore(dayjs(searchFilters.startTimeCST))) {
                                    message.error("End date cannot be lesser than start date");
                                    setSelectedKeys([]);
                                    handleReset(dataIndex, clearFilters);
                                    return;
                                }
                            }
                            setSelectedKeys([dateString]);
                        } else {
                            setSelectedKeys([]);
                        }
                    }}
                    disabledDate={disableFutureDates}
                    onPressEnter={() => confirm()}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => confirm()}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => {
                            handleReset(dataIndex, clearFilters);
                            confirm();      // Trigger table refresh
                        }}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        filteredValue: searchFilters[dataIndex] ? [searchFilters[dataIndex]] : null,
        sorter: false,
        sortOrder: sortedInfo.columnKey === dataIndex ? sortedInfo.order : null
    });

    const columns = [
        {
            title: 'File Name',
            dataIndex: 'fileName',
            key: 'fileName',
            width: 300,
            ...getColumnSearchProps("fileName"),
            render: (text, record) => (
                <Link
                    to="/processedIsbn"
                    state={{
                        fileId: record.fileId,
                        parentState: {
                            searchFilters,
                            sortedInfo,
                            pagination,
                            fromChild
                        } }}
                    style={{
                        color: '#1890ff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        textDecoration: 'underline'
                    }}
                >
                    <FileTextOutlined />
                    {text}
                </Link>
            ),
        },
        {
            title: "Total ISBN",
            dataIndex: "totalIsbn",
            key: "totalIsbn",
            width: 150
        },
        {
            title: "New ISBN",
            dataIndex: "newISBN",
            key: "newISBN",
            width: 100
        },
        {
            title: "Existing ISBN",
            dataIndex: "existingISBN",
            key: "existingISBN",
            width: 150
        },
        {
            title: "Duplicate ISBN",
            dataIndex: "duplicateISBN",
            key: "duplicateISBN",
            width: 150
        },
        {
            title: "Start Time in (CST)",
            dataIndex: "startTimeCST",
            key: "startTimeCST",
            width: 220,
            ...getDateColumnSearchProps("startTimeCST"),
        },
        {
            title: "Finish Time in (CST)",
            dataIndex: "finishTimeCST",
            key: "finishTimeCST",
            width: 220,
            ...getDateColumnSearchProps("finishTimeCST"),
        }
    ];

    const getRowClassName = (record, index) => {
        return index % 2 === 0 ? "odd-row" : "even-row";
    };

    const handleTableChange = (newPagination, filters = {}, sorter = {}) => {
        const normalizeFilters = {};
        Object.keys(filters).forEach((key) => {
            if (filters[key] && filters[key].length > 0) {
                normalizeFilters[key] = filters[key][0];
            }
        });
        const cleanSorter = Array.isArray(sorter) ? sorter[0] : sorter; // Handle multi-sort if applicable
        const serializableSorter = {
            field: cleanSorter.field,
            columnKey: cleanSorter.columnKey,
            order: cleanSorter.order,
        };

        setsearchFilters(normalizeFilters);
        setSortedInfo(serializableSorter);
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));

        fetchData({
            pagination: { current: newPagination.current, pageSize: newPagination.pageSize },
            sorter,
            filters: normalizeFilters,
        });
    };

    const handleExport = async () => {
        const payload = {
            pageNo: 1,
            pageSize: 0,
            search: JSON.stringify(searchFilters),
            orderByColumn: "CreatedOn",
            orderBy: "DESC",
        };
        const myHeaders = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
            "X-CSRF-Token": encryptTimestamp()
        };
        await runGridExport({
            request: fetch(CommonObj.Endpoints.GetLegalBatchList, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            }),
            exportFn: exportToExcel,
            columns,
            filename: 'Exception Log',
        });
    };

    const fetchData = async ({ pagination: pag, filters, sorter }) => {
        const payload = {
            pageNo: pag?.current,
            pageSize: pag?.pageSize || 10,
            search: JSON.stringify(filters),
            orderByColumn: sorter?.field || "CreatedOn",
            orderBy: sorter?.order === "ascend" ? "ASC" : "DESC",
        };
        setLoading(true);
        try {
            const myHeaders = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
                "X-CSRF-Token": encryptTimestamp(),
            };
            const response = await fetch(CommonObj.Endpoints.GetLegalBatchList, {
                method: "POST",
                headers: myHeaders,
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error("Network response was not ok");
            }
            const responseText = await response.text();
            let result;

            try {
                result = JSON.parse(responseText);
            } catch (e) {
                if (responseText.includes("No logs found")) {
                    result = [];
                } else {
                    throw new Error(responseText || 'Failed to parse API response.');
                }
            }
            const books = Array.isArray(result) ? result : [result];
            const formattedData = books.map((item) => ({
                ...item,
                key: item.isbn,
            }));

            setData(formattedData);
            const total = formattedData[0]?.totalRows || 0;
            setPagination((prev) => ({
                ...prev,
                current: pag?.current || prev.current,
                pageSize: pag?.pageSize || prev.pageSize,
                total,
            }));
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData({ pagination, sorter: sortedInfo, filters: searchFilters });
    }, []);

    if (loading) {
        return (
            <Spin size="large" style={{ margin: "20px auto" }} />
        );
    }
    const renderPaginationTotal = (total, range) =>
    (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: 8, position: 'absolute', left: '13rem' }}>
                {range[0]}-{range[1]} of {total} results
            </span>
            <div>
                <span style={{ marginRight: 4 }}> Go to page:</span>
                <Select
                    size="small"
                    value={pagination.current || 1}
                    className="custom-page-select"
                    onChange={(page) => {
                        console.log(page, "pagee")
                        const newPagination = { ...pagination, current: page };
                        handleTableChange(
                            newPagination,
                            searchFilters || {},
                            sortedInfo || {}
                        );
                    }}
                >
                    {Array.from({ length: Math.ceil(total / (pagination.pageSize || 10)) }, (_, i) => (
                        <Select.Option key={i + 1} value={i + 1}>
                            {i + 1}
                        </Select.Option>
                    ))}
                </Select>
            </div>
        </div>
    );

    return (
        <div className="active-page-container">
            <div className="active-page-header-container">
                <div className="active-page-header-title-container">
                    <h4>Legal Dataverifier Log</h4>
                </div>
            </div>
            <div className="active-page-body-container">
                <div className="rounded-table-wrapper">
                    <Table
                        columns={columns}
                        dataSource={data}
                        className="common-pagination common-grid"
                        pagination={{
                            ...pagination,
                            pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
                            showSizeChanger: true,
                            position: ["bottomRight"],
                            defaultPageSize: 10,
                            showTotal: renderPaginationTotal
                        }}
                        rowKey={(record) => record.isbn}
                        scroll={{ x: "max-content", y: "calc(100vh - 280px)" }}
                        rowClassName={getRowClassName}
                        onChange={handleTableChange}
                    />
                </div>
            </div>
        </div>
    );
};

export default LegalBatchService;
